import React, { FC, ReactNode, createContext, useContext, useMemo } from 'react';
import type { ISpAuthorizationToken } from 'ts/client/common';

export interface IClientGalleryServiceContextValue {
    spAuthorizationToken: ISpAuthorizationToken;
    spEventBus: SpAngularJs.ISpEventBus;
}

/**
 * Defaults to `null` so consumers can distinguish "rendered inside the real client gallery"
 * (where `ClientGalleryServiceProvider` supplies the Angular services below) from "rendered
 * somewhere without them" (e.g. the Studio branding preview) — allowing them to degrade
 * gracefully instead of crashing when the services aren't available.
 */
const ClientGalleryServiceContext = createContext<IClientGalleryServiceContextValue | null>(null);

interface IClientGalleryServiceProviderProps extends IClientGalleryServiceContextValue {
    children: ReactNode;
}

/**
 * Makes the Angular services needed to render an authenticated client-gallery slideshow
 * (`spAuthorizationToken`, `spEventBus`) available to the React tree via Context, rather than
 * threading them through every layout component. Mount once near the React root of a
 * client-gallery page (e.g. `LandingPage`) that receives these services from react2angular.
 */
export const ClientGalleryServiceProvider: FC<IClientGalleryServiceProviderProps> = ({
    spAuthorizationToken,
    spEventBus,
    children
}) => {
    const value = useMemo(
        () => ({ spAuthorizationToken, spEventBus }),
        [spAuthorizationToken, spEventBus]
    );

    return (
        <ClientGalleryServiceContext.Provider value={value}>
            {children}
        </ClientGalleryServiceContext.Provider>
    );
};

/**
 * Returns the client-gallery services, or `null` when rendered outside a
 * `ClientGalleryServiceProvider` (e.g. the Studio branding preview) — callers should treat
 * `null` as "degrade gracefully", not an error.
 */
export const useClientGalleryServiceContext = (): IClientGalleryServiceContextValue | null =>
    useContext(ClientGalleryServiceContext);
